<script setup>
import StatAdjustmentModal from './StatAdjustmentModal.vue'
import PlayerDetailsModal from './PlayerDetailsModal.vue'
import PlayerProfileModal from './PlayerProfileModal.vue'
import PlayerNewsModal from './PlayerNewsModal.vue'
import PlayerPositionModal from './PlayerPositionModal.vue'
import PlayerStatusModal from './PlayerStatusModal.vue'
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import { apiBalancedProjectionsPath } from '../config/balancedProjectionsApi.js'
import { useProjectionsStore } from '../stores/projections'
import { useRoute } from 'vue-router'

const projectionsStore = useProjectionsStore()

const route = useRoute()

const props = defineProps({
  player: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  statKeys: {
    type: Array,
    required: true
  },
  teamColor: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:player'])

const showAdjustmentModal = ref(false)
const adjustmentModalData = ref({ player: null, statKey: '', statLabel: '' })
const editingManualValues = ref({})
const showPlayerDetails = ref(false)
const showPlayerProfile = ref(false)
const showNewsModal = ref(false)
const showPositionModal = ref(false)
const showStatusModal = ref(false)

// PG=1, SG=2, SF=3, PF=4, C=5; first is highest level, unknown last
const POSITION_ORDER = { PG: 1, SG: 2, SF: 3, PF: 4, C: 5 }
const POSITION_ORDER_MAX = 99

function sortedPositionLabel(positionStr) {
  if (!positionStr || typeof positionStr !== 'string') return ''
  const parts = positionStr.split(',').map(p => p.trim()).filter(Boolean)
  if (!parts.length) return ''
  parts.sort((a, b) => {
    const orderA = POSITION_ORDER[a.toUpperCase()] ?? POSITION_ORDER_MAX
    const orderB = POSITION_ORDER[b.toUpperCase()] ?? POSITION_ORDER_MAX
    return orderA - orderB
  })
  return parts.join(', ')
}

const displayPosition = computed(() => sortedPositionLabel(props.player?.player?.position))

function openAdjustmentModal(player, statKey, statLabel, event) {
  adjustmentModalData.value = { player, statKey, statLabel }
  showAdjustmentModal.value = true
}

function closeAdjustmentModal() {
  showAdjustmentModal.value = false
}

function handleManualChange({ statKey, value }) {
  editingManualValues.value[statKey] = value
}

function getPerThirtyValue(statKey) {
  
  if (!props.player || !props.player.projection) return { value: '—', bg: 'bg-gray-200', text: 'text-black text-[12px]' }
  // console.log('player thrity', props.player)
  const proj = props.player.projection
  let value = null, bg = 'bg-gray-200', text = 'text-black text-[12px]'

  // Check for trim value first
  if (proj[`${statKey}_trim`] != null && proj[`${statKey}_trim`] !== '0.00' && proj[`${statKey}_trim`] !== 0) {
    value = proj[`${statKey}_trim`]
    bg = 'bg-blue-500'
    text = 'text-white text-[12px]'
  } else if (editingManualValues.value[statKey] !== undefined && editingManualValues.value[statKey] !== '' && editingManualValues.value[statKey] !== null) {
    value = editingManualValues.value[statKey]
    bg = 'bg-red-500'
    text = 'text-white text-[12px]'
  } else if (proj[`${statKey}_manual`] != null && proj[`${statKey}_manual`] !== '' && proj[`${statKey}_manual`] > 0 ) {
    value = proj[`${statKey}_manual`]
    bg = 'bg-red-500'
    text = 'text-white text-[12px]'
  } else if (proj[`${statKey}_auto`] != null && proj[`${statKey}_auto`] !== '' && proj[`${statKey}_auto`] > 0) {
    value = proj[`${statKey}_auto`]
    bg = 'bg-green-500'
    text = 'text-white text-[12px]'
  } else if (proj[`${statKey}_original`] != null) {
    value = proj[`${statKey}_original`]
    bg = 'bg-gray-200'
    text = 'text-black text-[12px]'
  } else {
    value = '—'
  }
  const num = Number(value)
  const display = value !== '—' && !isNaN(num) ? num.toFixed(2) : value
  return { value: display, bg, text }
}

function getRawStatValue(statKey) {
  if (!props.player || !props.player.projection) return '—'
  const proj = props.player.projection
  const perThirtyValue = getPerThirtyValue(statKey).value
  if (!proj || perThirtyValue === '—') return '—'
  
  const minutes = localProjectedMinutes.value
  if (!minutes) return '—'
  
  const num = Number(perThirtyValue)
  if (isNaN(num)) return perThirtyValue
  
  const rawValue = (num / 30) * minutes
  return rawValue.toFixed(2)
}

function openPlayerDetails(playerId) {
  showPlayerDetails.value = true
}

function closePlayerDetails() {
  showPlayerDetails.value = false
}

function openPlayerProfile(playerId) {
  showPlayerProfile.value = true
}

function closePlayerProfile() {
  showPlayerProfile.value = false
}

// Local reactive value for projected minutes input
const localProjectedMinutes = ref(0)
// Track original values to detect changes
const originalProjectedMinutes = ref(0)

// Initialize local value from store
watch(
  () => projectionsStore.getPlayerProjectedMinutes(props.player?.player?.id),
  (newValue) => {
    localProjectedMinutes.value = newValue
    // Only set original value on first load, not on subsequent updates
    if (originalProjectedMinutes.value === 0) {
      originalProjectedMinutes.value = newValue
    }
  },
  { immediate: true }
)

// Debounced function to update projected minutes
let updateTimeout = null
function updateProjectedMinutes(newValue) {
  // Clear existing timeout
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
  
  // Update store immediately for reactivity
  projectionsStore.updatePlayerProjectedMinutes(props.player.player.id, newValue)
  
  // Debounce API call
  updateTimeout = setTimeout(async () => {
    const oldValue = projectionsStore.getPlayerProjectedMinutes(props.player.player.id)
    
    // Only make request if value is valid
    if (newValue !== null && newValue !== undefined && newValue !== '') {
      try {
        await axios.post(apiBalancedProjectionsPath('projections/projected-minutes'), {
          playerId: props.player.player.id,
          projectedMinutes: newValue,
          fixtureId: projectionsStore.projectionsData.id
        })

        console.log('Successfully updated projected minutes:', newValue)
      } catch (error) {
        console.error('Failed to update projected minutes:', error)
        // Revert store on error
        projectionsStore.updatePlayerProjectedMinutes(props.player.player.id, oldValue)
        localProjectedMinutes.value = oldValue
      }
    }
  }, 500) // 500ms debounce
}

// Watch local value changes and update store
watch(localProjectedMinutes, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    updateProjectedMinutes(newValue)
  }
})

// Computed property to check if row has unsaved changes
const isDirty = computed(() => {
  // Check if projected minutes have changed from original value
  const minutesChanged = localProjectedMinutes.value !== originalProjectedMinutes.value
  
  // Check if there are any manual stat adjustments being edited
  const hasManualEdits = Object.keys(editingManualValues.value).length > 0
  
  return minutesChanged || hasManualEdits
})

// State to track threshold alerts for each stat
const thresholdAlerts = ref({})

const checkStatThresholdsManualTrigger = async (playerId) => {
  props.statKeys.forEach(stat => {
    checkStatThresholds(stat.key)
  })
}

// Expose the function to parent components
defineExpose({
  checkStatThresholdsManualTrigger
})

// Function to check thresholds for a specific stat
const checkStatThresholds = async (statKey) => {
  try {
    const response = await axios.post(apiBalancedProjectionsPath('projections/get-additional-stats-by-days'), {
      fixture_id: route.params.id,
      player_id: props.player.player.id,
      stat_key: statKey,
      days: 10
    })

    if (response.data?.last_games_per_30) {
      // Get the actual current value being displayed (could be original, auto, manual, or trim)
      const currentValue = parseFloat(getPerThirtyValue(statKey).value) || 0
      
      if (currentValue > 0) {
        // Check last 3 games threshold (>= 50%)
        const last3Value = parseFloat(response.data.last_games_per_30.last_3_games_per_30?.[statKey]) || 0
        const last3Diff = Math.abs(((last3Value - currentValue) / currentValue) * 100)
        
        // Check last 6 games threshold (>= 30%)
        const last6Value = parseFloat(response.data.last_games_per_30.last_6_games_per_30?.[statKey]) || 0
        const last6Diff = Math.abs(((last6Value - currentValue) / currentValue) * 100)
        
        // Check last 10 games threshold (>= 20%)
        const last10Value = parseFloat(response.data.last_games_per_30.last_10_games_per_30?.[statKey]) || 0
        const last10Diff = Math.abs(((last10Value - currentValue) / currentValue) * 100)
        
        // Update threshold alerts
        thresholdAlerts.value[statKey] = last3Diff >= 1000 || last6Diff >= 40 || last10Diff >= 20
      }
    }
  } catch (error) {
    console.error('Error checking thresholds for stat:', statKey, error)
    thresholdAlerts.value[statKey] = false
  }
}

// Check thresholds for all stats when component mounts
//onMounted(() => {
//  props.statKeys.forEach(stat => {
//    checkStatThresholds(stat.key)
//  })
//})

// Re-check thresholds when displayed values change (e.g., manual adjustments)
//watch(() => props.player.projection, () => {
//  props.statKeys.forEach(stat => {
//    checkStatThresholds(stat.key)
//  })
//}, { deep: true })

// Re-check thresholds when manual editing values change
//watch(editingManualValues, () => {
//  props.statKeys.forEach(stat => {
//    checkStatThresholds(stat.key)
//  })
//}, { deep: true })

const updateRowData = async () => {
  console.log('Updating row data')
  try {
    await axios.post(apiBalancedProjectionsPath('projections/update-player-row'), {
      playerId: props.player.player.id,
      fixtureId: projectionsStore.projectionsData.id
    })
  } catch (error) {
    console.error('Failed to update row data:', error)
  } finally {
    isDirty.value = false
  }
}

const toggleStarterStatus = async () => {
  // TODO: Implement API call to update starter status
  try {
    await axios.post(apiBalancedProjectionsPath('projections/update-player-starter-status'), {
      fixtureId: route.params.id,
      playerId: props.player.player.id,
    })
  } catch (error) {
    console.error('Failed to update starter status:', error)
  }
}

// Context menu state
const contextMenuVisible = ref(false)
const contextMenuPosition = ref({ x: 0, y: 0 })

// Handle right-click on player name
function handleContextMenu(event) {
  event.preventDefault()
  event.stopPropagation()
  
  contextMenuPosition.value = {
    x: event.clientX,
    y: event.clientY
  }
  contextMenuVisible.value = true
}

// Close context menu when clicking outside
function closeContextMenu() {
  contextMenuVisible.value = false
}

// Handle context menu item clicks
function handleMenuAction(action) {
  closeContextMenu()
  
  switch (action) {
    case 'view-details':
      openPlayerProfile(props.player.player.id)
      break
    case 'toggle-starter':
      toggleStarterStatus()
      break
    case 'update-row':
      updateRowData()
      break
    case 'check-thresholds':
      checkStatThresholdsManualTrigger(props.player.player.id)
      break
    case 'news':
      openNewsModal()
      break
    case 'copy-name':
      navigator.clipboard.writeText(props.player.player.player_name)
      break
    case 'copy-id':
      navigator.clipboard.writeText(props.player.player.id.toString())
      break
    case 'edit-position':
      openPositionModal()
      break
    case 'edit-status':
      openStatusModal()
      break
  }
}

// News modal functions
function openNewsModal() {
  showNewsModal.value = true
}

function closeNewsModal() {
  showNewsModal.value = false
}

// Position modal functions
function openPositionModal() {
  showPositionModal.value = true
}

function closePositionModal() {
  showPositionModal.value = false
}

// Status modal functions
function openStatusModal() {
  showStatusModal.value = true
}

function closeStatusModal() {
  showStatusModal.value = false
}

// Hover tooltip: last news message over the row
const hoverNewsTooltipVisible = ref(false)
const hoverNewsContent = ref(null) // { text, author, date }
const hoverNewsLoading = ref(false)
let hoverNewsTimeout = null

function stripHtml(html) {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent || div.innerText || '').trim()
}

// News popup on hover - only fetch when player has unread news (to avoid too many requests)
function onRowMouseEnter() {
  if (!props.player?.player?.has_unread_news) return
  hoverNewsTimeout = setTimeout(async () => {
    if (!props.player?.player?.id) return
    hoverNewsLoading.value = true
    hoverNewsTooltipVisible.value = true
    try {
      const response = await axios.get(`/api/players/${props.player.player.id}/news`)
      const list = response.data?.news ?? (Array.isArray(response.data) ? response.data : [])
      const last = list.length ? list[list.length - 1] : null
      if (last) {
        hoverNewsContent.value = {
          text: stripHtml(last.message || ''),
          author: last.author || 'System',
          date: last.created_at || last.timestamp
        }
      } else {
        hoverNewsContent.value = null
      }
    } catch (_) {
      hoverNewsContent.value = null
    } finally {
      hoverNewsLoading.value = false
    }
  }, 400)
}

function onRowMouseLeave() {
  if (hoverNewsTimeout) {
    clearTimeout(hoverNewsTimeout)
    hoverNewsTimeout = null
  }
  hoverNewsTooltipVisible.value = false
  hoverNewsContent.value = null
  hoverNewsLoading.value = false
}

function formatNewsDate(timestamp) {
  if (!timestamp) return ''
  const d = new Date(timestamp)
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

// Close context menu on outside click
onMounted(() => {
  document.addEventListener('click', closeContextMenu)
})

onUnmounted(() => {
  document.removeEventListener('click', closeContextMenu)
  if (hoverNewsTimeout) clearTimeout(hoverNewsTimeout)
})

// Computed property to check if player has oddsmarket_player_id
const hasOddsmarketPlayerId = computed(() => {
  const mappings = props.player?.player?.monitoring_tool_player_mappings
  if (!mappings || !Array.isArray(mappings) || mappings.length === 0) {
    return false
  }
  const firstMapping = mappings[0]
  return firstMapping?.oddsmarket_player_id != null && firstMapping?.oddsmarket_player_id !== ''
})

// Computed property to determine if green bubble should be shown
const showGreenBubble = computed(() => {
  return projectionsStore.showMonitoringToolMapping && hasOddsmarketPlayerId.value
})

// Computed property to determine if red bubble should be shown
const showRedBubble = computed(() => {
  return projectionsStore.showMonitoringToolMapping && !hasOddsmarketPlayerId.value
})

// Get player status ID - prioritize new player_team_status.player_status_id, fallback to old player_status.id
function getPlayerStatusId() {
  if (!props.player?.player) return null
  
  // Prioritize the new player_team_status.player_status_id (same as PlayerStatusDropdown uses)
  // Only fallback to old player_status.id when new version is missing
  if (props.player.player.player_team_status?.player_status_id !== undefined && props.player.player.player_team_status?.player_status_id !== null) {
    return props.player.player.player_team_status.player_status_id
  }
  // Fallback to old version only when new version is missing
  if (props.player.player.player_status?.id !== undefined && props.player.player.player_status?.id !== null) {
    return props.player.player.player_status.id
  }
  return null
}

// Get status letter: 1=A (Active), 2=Q (Questionable), 3=O (Out), 4=P (Probable), 5=D (Doubtful)
function getStatusLetter(statusId) {
  if (!statusId) return ''
  
  if (statusId === 1) return 'A' // Active
  if (statusId === 2) return 'Q' // Questionable
  if (statusId === 3) return 'O' // Out
  if (statusId === 4) return 'P' // Probable
  if (statusId === 5) return 'D' // Doubtful
  
  return ''
}

// Get status badge color class: 1=Active (green), 2=Questionable (orange), 3=Out (red), 4=Probable (light-green), 5=Doubtful (yellow)
function getStatusBadgeClass(statusId) {
  if (!statusId) return 'bg-gray-200 text-gray-800'
  
  if (statusId === 1) return 'bg-green-600 text-white' // Active
  if (statusId === 2) return 'bg-orange-500 text-white' // Questionable
  if (statusId === 3) return 'bg-red-600 text-white' // Out
  if (statusId === 4) return 'bg-green-400 text-white' // Probable
  if (statusId === 5) return 'bg-yellow-500 text-white' // Doubtful
  
  return 'bg-gray-200 text-gray-800'
}
</script>

<template>
    <div class="grid grid-cols-[50px_1fr_20px_100px_repeat(9,1fr)] gap-2 p-1 hover:bg-gray-50 transition-colors group relative"
       :class="[index % 2 === 0 ? 'bg-white' : 'bg-gray-50', `hover:bg-${teamColor}-50`]"
       @mouseenter="onRowMouseEnter"
       @mouseleave="onRowMouseLeave">
        <!-- Hover tooltip: last news message above the row -->
        <div
          v-if="hoverNewsTooltipVisible"
          class="absolute left-0 bottom-full z-20 mb-1 flex"
        >
          <div class="min-w-0 max-w-md text-left text-sm text-gray-700 bg-white shadow-lg border border-gray-200 rounded-lg px-3 py-2 pointer-events-none">
            <template v-if="hoverNewsLoading">
              <span class="text-gray-500">Loading…</span>
            </template>
            <template v-else-if="hoverNewsContent">
              <div class="text-xs text-gray-500 mb-0.5">{{ hoverNewsContent.author }} · {{ formatNewsDate(hoverNewsContent.date) }}</div>
              <div class="line-clamp-2 break-words">{{ hoverNewsContent.text || 'No message' }}</div>
            </template>
            <template v-else>
              <span class="text-gray-500">No news yet</span>
            </template>
          </div>
        </div>
        <!-- <div v-if="isDirty" class="absolute top-0 h-full flex items-center justify-center left-0">
        <button @click="updateRowData" class="bg-green-500/90 text-white px-1 py-0 rounded-md">Update</button>
       </div> -->
    <div 
      class="h-full flex items-center cursor-pointer" 
      :class="(showGreenBubble || showRedBubble) ? 'justify-center' : 'pl-2'"
      @click="checkStatThresholdsManualTrigger(player.player.id)"
    >
      <span 
        v-if="showGreenBubble"
        class="bg-green-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-semibold"
      >
        {{ index + 1 }}
      </span>
      <span 
        v-else-if="showRedBubble"
        class="bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-semibold"
      >
        {{ index + 1 }}
      </span>
      <span v-else>{{ index + 1 }}</span>
    </div>
    <div class="font-medium text-gray-800 h-full justify-start gap-1 flex items-center  cursor-pointer hover:underline relative" 
         :class="`group-hover:text-${teamColor}-700`"
         @click="openPlayerDetails(player.player.id)"
         @contextmenu="handleContextMenu"
         data-no-global-context-menu>
         <!-- {{ player.player.player_status }} -->
   
     
         <div class="flex items-center gap-2">
           <div class="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                :class="getStatusBadgeClass(getPlayerStatusId())">
             <span class="text-xs font-semibold leading-none">{{ getStatusLetter(getPlayerStatusId()) }}</span>
            </div>
           <span class="inline-block align-middle flex items-center gap-1">
            <!-- {{ player }} -->
             {{ player.player?.player_name }}
             <span class="text-xs text-gray-400" v-if="displayPosition">
               - {{ displayPosition }}
             </span>
             <!-- Envelope icon when player has unread news - before per_game_minutes -->
             <span
               v-if="player.player?.has_unread_news"
               class="ml-1 text-red-500 shrink-0 inline-flex"
               title="Unread news"
             >
               <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                 <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                 <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
               </svg>
             </span>
             <span
               v-if="player.player?.projections_settings_player_minutes_settings?.per_game_minutes"
               class="ml-2 px-2 py-0.5 rounded text-xs text-right"
               style="min-width:48px; display:inline-block;"
             >
               {{ player.player?.projections_settings_player_minutes_settings?.per_game_minutes ?? '—' }}
             </span>
           </span>
         </div>
      
      <!-- Context Menu -->
      <div v-if="contextMenuVisible" 
           class="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px]"
           :style="{ left: contextMenuPosition.x + 'px', top: contextMenuPosition.y + 'px' }"
           @click.stop>
        <button @click="handleMenuAction('view-details')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Player profile
        </button>
        
        <button @click="handleMenuAction('toggle-starter')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ player.projection?.is_starter ? 'Remove from Starters' : 'Add to Starters' }}
        </button>
        
        <!-- <button @click="handleMenuAction('check-thresholds')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Check Thresholds
        </button> -->
        
        <button @click="handleMenuAction('news')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <span class="relative">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <!-- Unread indicator dot on News button -->
            <span
              v-if="player.player?.has_unread_news"
              class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full"
              title="Unread news"
            />
          </span>
          News
        </button>
        
        <button @click="handleMenuAction('edit-position')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit Position
        </button>
        
        <button @click="handleMenuAction('edit-status')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Edit Status
        </button>
        
        <!-- <button v-if="isDirty" @click="handleMenuAction('update-row')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Update Row Data
        </button> -->
        
        <div class="border-t border-gray-200 my-1"></div>
        <div @click.stop
             class="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer"
             @click="projectionsStore.toggleMonitoringToolMapping()">
          <input 
            type="checkbox" 
            :checked="projectionsStore.showMonitoringToolMapping"
            @change="projectionsStore.toggleMonitoringToolMapping()"
            @click.stop
            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
          />
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01"/>
          </svg>
          Monitoring tool all player mappings
        </div>
        <button @click="handleMenuAction('copy-name')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy Name
        </button>
        
        <button @click="handleMenuAction('copy-id')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
          </svg>
          Copy P.ID {{ player.player?.id }}
        </button>
      </div>
    </div>
    <div class="flex items-center gap-2 ml-2">
      <input 
        type="checkbox" 
        :checked="player.projection?.is_starter" 
        @change="toggleStarterStatus"
        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
      />
    </div>
    <!-- <div class="text-center h-full flex items-center justify-center">{{ player.season_avg_minutes ?? '—' }}</div> -->
    <div class="text-center h-full flex items-center">
      
        <input type="number" 
              v-model="localProjectedMinutes" 
              :class="`w-16 md:w-20 border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-${teamColor}-400 focus:border-${teamColor}-400 transition outline-none text-center bg-gray-50 hover:bg-white`" />
    </div>
    <div v-for="stat in statKeys" :key="stat.key" class="text-center  flex justify-center relative">
              <div class="flex items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 rounded-md pl-1 transition-all duration-200 ease-in-out group">
          <span class="text-gray-800 text-lg group-hover:text-gray-900 transition-colors">
            <!-- {{ getRawStatValue(stat.key) }} -->
        <span v-if="stat.key !== 'pra_per_30'">{{ projectionsStore.getFormattedProjectionValue(player.player?.id, stat.key) }} </span>
       
       <span v-if="stat.key === 'pra_per_30'">{{ (  Number(projectionsStore.getFormattedProjectionValue(player.player?.id, 'points_per_30')) + Number(projectionsStore.getFormattedProjectionValue(player.player?.id, 'total_rebounds_per_30')) + Number(projectionsStore.getFormattedProjectionValue(player.player?.id, 'assists_per_30'))).toFixed(2) }}</span>   


          </span>
        <span v-bind:class="[getPerThirtyValue(stat.key).bg, getPerThirtyValue(stat.key).text, 'rounded-full px-2 py-0.5 text-xs font-semibold inline-block min-w-[2.5rem] cursor-pointer transform hover:scale-105 hover:shadow-md transition-all duration-200 ease-in-out']"
              @click="openAdjustmentModal(player, stat.key, stat.label, $event)">
          {{ getPerThirtyValue(stat.key).value }}
        </span>
        <span v-if="player.projection && player.projection[stat.key + '_original'] != null" 
              class="text-gray-500 text-xs ml-1" 
              title="Original per 30">
          ({{ Number(player.projection[stat.key + '_original']).toFixed(2) }})
        </span>
        <span v-if="thresholdAlerts[stat.key]" class="inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full ml-1" title="Threshold alert: Significant deviation detected">
          !
        </span>
        
        <StatAdjustmentModal
          v-if="showAdjustmentModal && adjustmentModalData.player?.player?.id === player.player.id && adjustmentModalData.statKey === stat.key"
          :player="player"
          :stat-key="stat.key"
          :stat-label="stat.label"
          @close="closeAdjustmentModal"
          @update:player="(updatedPlayer) => emit('update:player', updatedPlayer)"
          @manual-change="handleManualChange"
        />
      </div>
    </div>
  </div>

  <PlayerDetailsModal
    :player-id="player.player?.id"
    :show="showPlayerDetails"
    @close="closePlayerDetails"
  />

  <PlayerProfileModal
    :player-id="player.player?.id"
    :show="showPlayerProfile"
    @close="closePlayerProfile"
  />

  <PlayerNewsModal
    :player-id="player.player?.id"
    :player-name="player.player?.player_name"
    :fixture-id="route.params.id"
    :show="showNewsModal"
    @close="closeNewsModal"
    @status-updated="(status) => console.log('Status updated:', status)"
  />

  <PlayerPositionModal
    v-if="showPositionModal"
    :player="player"
    @close="closePositionModal"
    @update:player="(updatedPlayer) => emit('update:player', updatedPlayer)"
  />

  <PlayerStatusModal
    v-if="showStatusModal"
    :player="player"
    @close="closeStatusModal"
    @update:player="(updatedPlayer) => emit('update:player', updatedPlayer)"
  />
</template> 